
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import axios from "axios";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { baseUrl } from "../Univ.js";

import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from "@mui/material";

// =========================================================
// CATEGORY CONFIGURATION
// =========================================================

const CATEGORIES = [
    "Consulting",
    "Lab",
    "CT",
    "Xray",
    "Scan",
    "Dialysis",
    "Inj",
    "Obs",
    "Echo",
    "Uroflow",
    "Minor",
    "Audiology",
    "Dental",
];

const HIDDEN_CATEGORIES = ["Audiology", "Dental"];

// Minor category IDs from main_mincat.
// Do not change the IDs unless the database values change.
const MINOR_CATEGORY_MAP = {
    8: "Dialysis",
    12: "Inj",
    13: "Obs",
    14: "Echo",
    15: "Uroflow",
};

// =========================================================
// COMMON HELPERS
// =========================================================

const toNumber = (value) => {
    const number = Number(value ?? 0);
    return Number.isFinite(number) ? number : 0;
};

const money = (value) =>
    toNumber(value).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const normalize = (value) =>
    String(value ?? "").trim().toLowerCase();

const formatDate = (date) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const getInvoiceNumber = (item) =>
    item.invoice ||
    item.lab_invoice ||
    item.inv_invoice ||
    item.min_invoice ||
    "";

const getInvoiceLink = (item) => {
    if (item.invoice) {
        return `/op-invoice/${item.invoice}`;
    }

    if (item.lab_invoice) {
        return `/lab-invoice-op/${item.lab_invoice}`;
    }

    if (item.inv_invoice) {
        return `/inv-invoice-op/${item.inv_invoice}`;
    }

    if (item.min_invoice) {
        return `/min-invoice-op/${item.min_invoice}`;
    }

    return "";
};

const getInvoiceType = (item) => {
    if (item.invoice) return "consulting";
    if (item.lab_invoice) return "lab";
    if (item.inv_invoice) return "imaging";
    if (item.min_invoice) return "minor";

    return "unknown";
};

const getCategoryAmounts = () => {
    const result = {};

    CATEGORIES.forEach((category) => {
        result[category] = {
            total: 0,
            paid: 0,
            balance: 0,
            discount: 0,
        };
    });

    return result;
};

// =========================================================
// MINOR CATEGORY CLASSIFICATION
// =========================================================

const getMinorCategory = (component) => {
    const category = component?.cat;

    if (!category) return "Minor";

    const categoryId = Number(category.id);

    if (MINOR_CATEGORY_MAP[categoryId]) {
        return MINOR_CATEGORY_MAP[categoryId];
    }

    const categoryName = normalize(category.cat);

    if (categoryName.includes("audiology")) {
        return "Audiology";
    }

    if (categoryName.includes("dental")) {
        return "Dental";
    }

    if (categoryName.includes("dialysis")) {
        return "Dialysis";
    }

    if (categoryName.includes("inj")) {
        return "Inj";
    }

    if (categoryName.includes("obs")) {
        return "Obs";
    }

    if (categoryName.includes("echo")) {
        return "Echo";
    }

    if (categoryName.includes("uroflow")) {
        return "Uroflow";
    }

    return "Minor";
};

// =========================================================
// DISTRIBUTE MINOR INVOICE AMOUNTS BY COMPONENT CATEGORY
// =========================================================

const distributeMinorAmounts = (item, components, amounts) => {
    const grouped = {};

    components.forEach((component) => {
        const category = getMinorCategory(component);

        if (!grouped[category]) {
            grouped[category] = 0;
        }

        grouped[category] += toNumber(component.amount);
    });

    const entries = Object.entries(grouped);

    if (!entries.length) {
        amounts.Minor = {
            total: toNumber(item.min_tot),
            paid: toNumber(item.min_paid),
            balance: toNumber(item.min_balance),
            discount: toNumber(item.min_disc),
        };

        return;
    }

    const totalWeight = entries.reduce(
        (sum, [, weight]) => sum + weight,
        0
    );

    const fields = [
        {
            key: "total",
            value: toNumber(item.min_tot),
        },
        {
            key: "paid",
            value: toNumber(item.min_paid),
        },
        {
            key: "balance",
            value: toNumber(item.min_balance),
        },
        {
            key: "discount",
            value: toNumber(item.min_disc),
        },
    ];

    fields.forEach(({ key, value }) => {
        let distributed = 0;

        entries.forEach(([category, weight], index) => {
            let allocated = 0;

            if (index === entries.length - 1) {
                allocated = value - distributed;
            } else if (totalWeight > 0) {
                allocated =
                    (value * weight) / totalWeight;

                distributed += allocated;
            } else {
                allocated = value / entries.length;
                distributed += allocated;
            }

            amounts[category][key] += allocated;
        });
    });
};

// =========================================================
// MAIN COMPONENT
// =========================================================

function CollectionAll() {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const [cashier, setCashier] = useState("");
    const [consultant, setConsultant] = useState("all");
    const [mode, setMode] = useState("");

    const [userData, setUserData] = useState([]);
    const [combinedData, setCombinedData] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    // =====================================================
    // LOAD CASHIER LIST
    // =====================================================

    useEffect(() => {
        let cancelled = false;

        axios
            .get(`${baseUrl}user-search/`)
            .then((response) => {
                if (!cancelled) {
                    setUserData(
                        Array.isArray(response.data)
                            ? response.data
                            : []
                    );
                }
            })
            .catch((err) => {
                console.error(
                    "Unable to load cashier list:",
                    err
                );
            });

        return () => {
            cancelled = true;
        };
    }, []);

    // =====================================================
    // FETCH COLLECTION DATA
    // =====================================================

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError("");

        try {
            const response = await axios.get(
                `${baseUrl}all-collection-view/`,
                {
                    params: {
                        start_date: formatDate(startDate),
                        end_date: formatDate(endDate),
                        cashier,
                        mode,
                        status: 2,
                    },
                }
            );

            const data = Array.isArray(response.data)
                ? response.data
                : [];

            const formattedData = await Promise.all(
                data.map(async (item, index) => {
                    const minIdArray = item.min_id
                        ? String(item.min_id)
                              .split(",")
                              .map((id) => id.trim())
                              .filter(Boolean)
                        : [];

                    const invIdArray = item.itest_id
                        ? String(item.itest_id)
                              .split(",")
                              .map((id) => id.trim())
                              .filter(Boolean)
                        : [];

                    // Fetch minor and imaging component details.
                    const [minorResponses, invResponses] =
                        await Promise.all([
                            Promise.all(
                                minIdArray.map((id) =>
                                    axios.get(
                                        `${baseUrl}edit-minor-comp/${id}`
                                    )
                                )
                            ),

                            Promise.all(
                                invIdArray.map((id) =>
                                    axios.get(
                                        `${baseUrl}edit-components/${id}`
                                    )
                                )
                            ),
                        ]);

                    const minorComponents = minorResponses.map(
                        (res) => res.data
                    );

                    const invData = invResponses
                        .map((res) => res.data?.cat)
                        .filter(Boolean);

                    const amounts = getCategoryAmounts();

                    // -----------------------------------------
                    // CONSULTING
                    // -----------------------------------------

                    amounts.Consulting = {
                        total: toNumber(
                            item.fee ??
                                toNumber(item.paid) +
                                    toNumber(item.balance)
                        ),
                        paid: toNumber(item.paid),
                        balance: toNumber(item.balance),
                        discount: toNumber(item.disc),
                    };

                    // -----------------------------------------
                    // LAB
                    // -----------------------------------------

                    amounts.Lab = {
                        total: toNumber(item.lab_tot),
                        paid: toNumber(item.lab_paid),
                        balance: toNumber(item.lab_balance),
                        discount: toNumber(item.lab_disc),
                    };

                    // -----------------------------------------
                    // MINOR CATEGORIES
                    // Inj / Obs / Echo / Uroflow /
                    // Dialysis / Audiology / Dental / Minor
                    // -----------------------------------------

                    distributeMinorAmounts(
                        item,
                        minorComponents,
                        amounts
                    );

                    // -----------------------------------------
                    // IMAGING CATEGORIES
                    // -----------------------------------------

                    if (invData.includes("CT")) {
                        amounts.CT = {
                            total: toNumber(item.inv_tot),
                            paid: toNumber(item.inv_paid),
                            balance: toNumber(item.inv_balance),
                            discount: toNumber(item.inv_disc),
                        };
                    } else if (
                        invData.includes("Xray") ||
                        invData.includes("X-ray")
                    ) {
                        amounts.Xray = {
                            total: toNumber(item.inv_tot),
                            paid: toNumber(item.inv_paid),
                            balance: toNumber(item.inv_balance),
                            discount: toNumber(item.inv_disc),
                        };
                    } else if (
                        invData.includes("USG") ||
                        invData.includes("Scan")
                    ) {
                        amounts.Scan = {
                            total: toNumber(item.inv_tot),
                            paid: toNumber(item.inv_paid),
                            balance: toNumber(item.inv_balance),
                            discount: toNumber(item.inv_disc),
                        };
                    }

                    // -----------------------------------------
                    // INVOICE DETAILS
                    // -----------------------------------------

                    const invoiceNumber = getInvoiceNumber(item);
                    const invoiceLink = getInvoiceLink(item);
                    const invoiceType = getInvoiceType(item);

                    const rowId = [
                        invoiceType,
                        item.id ?? index,
                        invoiceNumber || item.appt_id || "",
                    ].join("-");

                    return {
                        ...item,

                        id: rowId,

                        date: item.add_date_date ? `${item.add_date_date} ${item.add_time || ""}`.trim() : "",
                        appointmentId: item.appt_id || "",
                        patientId: item.pat__pat_id || "",
                        patientName: item.pat__patient || "",

                        consultantName:
                            item.cons__consultant ||
                            item.consultant ||
                            "",

                        cashierName: item.cashier || "",

                        invoice: invoiceNumber,
                        invoiceLink,

                        fee: amounts.Consulting.paid,
                        lab: amounts.Lab.paid,

                        mode:
                            item.mode ||
                            item.lab_pmode ||
                            item.inv_pmode ||
                            item.min_pmode ||
                            "",

                        categoryAmounts: amounts,

                        minorComponents,
                        invData,
                    };
                })
            );

            setCombinedData(formattedData);
        } catch (err) {
            console.error(
                "Unable to load collection report:",
                err
            );

            setError(
                err.response?.data?.detail ||
                    err.response?.data?.error ||
                    "Unable to load the collection report. Please try again."
            );

            setCombinedData([]);
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate, cashier, mode]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // =====================================================
    // CONSULTANT FILTER OPTIONS
    // =====================================================

    const consultants = useMemo(() => {
        const names = combinedData
            .map((row) => row.consultantName)
            .filter(Boolean);

        return [...new Set(names)].sort((a, b) =>
            a.localeCompare(b)
        );
    }, [combinedData]);

    // =====================================================
    // COLLECTION ROWS
    // =====================================================

    const filteredData = useMemo(() => {
        return combinedData.filter((row) => {
            const consultantMatch =
                consultant === "all" ||
                normalize(row.consultantName) ===
                    normalize(consultant);

            const hasCollection = CATEGORIES.some(
                (category) =>
                    toNumber(
                        row.categoryAmounts?.[category]?.paid
                    ) > 0
            );

            return consultantMatch && hasCollection;
        });
    }, [combinedData, consultant]);

    // =====================================================
    // COLLECTION CATEGORY TOTALS
    // =====================================================

    const categoryTotals = useMemo(() => {
        const totals = {};

        CATEGORIES.forEach((category) => {
            totals[category] = {
                paid: 0,
                discount: 0,
                total: 0,
            };
        });

        filteredData.forEach((row) => {
            CATEGORIES.forEach((category) => {
                const categoryData =
                    row.categoryAmounts?.[category] || {};

                totals[category].paid += toNumber(
                    categoryData.paid
                );

                totals[category].discount += toNumber(
                    categoryData.discount
                );

                totals[category].total += toNumber(
                    categoryData.total
                );
            });
        });

        totals.grandTotal = CATEGORIES.reduce(
            (sum, category) =>
                sum + totals[category].paid,
            0
        );

        totals.grandDiscount = CATEGORIES.reduce(
            (sum, category) =>
                sum + totals[category].discount,
            0
        );

        return totals;
    }, [filteredData]);

    // =====================================================
    // PAYMENT MODE TOTALS
    // =====================================================

    const paymentModeTotals = useMemo(() => {
        const totals = {
            cash: 0,
            card: 0,
            creditpay: 0,
            UPI: 0,
            other: 0,
        };

        filteredData.forEach((row) => {
            const rowTotal = CATEGORIES.reduce(
                (sum, category) =>
                    sum +
                    toNumber(
                        row.categoryAmounts?.[category]?.paid
                    ),
                0
            );

            const paymentMode = normalize(row.mode);

            if (paymentMode === "cash") {
                totals.cash += rowTotal;
            } else if (paymentMode === "card") {
                totals.card += rowTotal;
            } else if (paymentMode === "creditpay") {
                totals.creditpay += rowTotal;
            } else if (paymentMode === "upi") {
                totals.UPI += rowTotal;
            } else {
                totals.other += rowTotal;
            }
        });

        return totals;
    }, [filteredData]);

    // =====================================================
    // DATAGRID COLUMNS
    // =====================================================

    const columns = useMemo(() => {
        return [
            {
                field: "sno",
                headerName: "S.No",
                width: 65,
                sortable: false,
                filterable: false,
                renderCell: (params) =>
                    params.api.getRowIndexRelativeToVisibleRows(
                        params.id
                    ) + 1,
            },
            {
                field: "date",
                headerName: "Date",
                width: 110,
            },
            {
                field: "appointmentId",
                headerName: "Appointment ID",
                width: 130,
            },
            {
                field: "patientId",
                headerName: "Patient ID",
                width: 105,
            },
            {
                field: "patientName",
                headerName: "Patient Name",
                width: 170,
            },
            {
                field: "consultantName",
                headerName: "Consultant",
                width: 165,
                valueGetter: (_value, row) =>
                    row.consultantName || "—",
            },
            {
                field: "invoice",
                headerName: "Invoice No",
                width: 150,
                sortable: false,
                renderCell: (params) =>
                    params.row.invoiceLink ? (
                        <Link
                            to={params.row.invoiceLink}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {params.row.invoice}
                        </Link>
                    ) : (
                        params.row.invoice || "—"
                    ),
            },

            ...CATEGORIES.map((category) => ({
                field: `category_${category}`,
                headerName: category,
                width: 115,
                type: "number",
                valueGetter: (_value, row) =>
                    toNumber(
                        row.categoryAmounts?.[category]?.paid
                    ),
                renderCell: (params) =>
                    money(params.value),
            })),

            {
                field: "discountTotal",
                headerName: "Discount",
                width: 120,
                type: "number",
                valueGetter: (_value, row) =>
                    CATEGORIES.reduce(
                        (sum, category) =>
                            sum +
                            toNumber(
                                row.categoryAmounts?.[category]
                                    ?.discount
                            ),
                        0
                    ),
                renderCell: (params) =>
                    money(params.value),
            },
            {
                field: "mode",
                headerName: "Mode",
                width: 110,
            },
            {
                field: "cashierName",
                headerName: "Cashier",
                width: 130,
            },
        ];
    }, []);

    // =====================================================
    // CLEAR FILTERS
    // =====================================================

    const clearFilters = () => {
        setStartDate(null);
        setEndDate(null);
        setCashier("");
        setConsultant("all");
        setMode("");
    };

    // =====================================================
    // FOOTER TOTALS
    // =====================================================

    const footerTotals = useMemo(() => {
        const totals = {};

        CATEGORIES.forEach((category) => {
            totals[category] = 0;
        });

        filteredData.forEach((row) => {
            CATEGORIES.forEach((category) => {
                totals[category] += toNumber(
                    row.categoryAmounts?.[category]?.paid
                );
            });
        });

        return totals;
    }, [filteredData]);

    // =====================================================
    // PAGE UI
    // =====================================================

    return (
        <Box className="payment-ip-page">

            {/* PAGE HEADER */}

            <Box className="payment-ip-header">
                <Box>
                    <Typography
                        variant="h5"
                        className="payment-ip-title"
                    >
                        OP Collection Report
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Outpatient payment collection details
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    onClick={fetchData}
                    disabled={isLoading}
                >
                    Refresh
                </Button>
            </Box>

            {/* FILTER CARD */}

            <Card className="payment-ip-filter-card">
                <CardContent>
                    <Grid
                        container
                        spacing={2}
                        alignItems="center"
                    >

                        {/* FROM DATE */}

                        <Grid item xs={12} sm={6} md={2}>
                            <Typography
                                variant="body2"
                                sx={{ mb: 0.6 }}
                            >
                                From Date
                            </Typography>

                            <DatePicker
                                selected={startDate}
                                onChange={setStartDate}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                maxDate={endDate || undefined}
                                placeholderText="From Date"
                                dateFormat="dd/MM/yyyy"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                isClearable
                                className="date-picker-input"
                                wrapperClassName="payment-ip-date-wrapper"
                            />
                        </Grid>

                        {/* TO DATE */}

                        <Grid item xs={12} sm={6} md={2}>
                            <Typography
                                variant="body2"
                                sx={{ mb: 0.6 }}
                            >
                                To Date
                            </Typography>

                            <DatePicker
                                selected={endDate}
                                onChange={setEndDate}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate || undefined}
                                placeholderText="To Date"
                                dateFormat="dd/MM/yyyy"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                isClearable
                                className="date-picker-input"
                                wrapperClassName="payment-ip-date-wrapper"
                            />
                        </Grid>

                        {/* CASHIER */}

                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl
                                fullWidth
                                size="small"
                            >
                                <InputLabel id="collection-cashier-label">
                                    Search by Cashier
                                </InputLabel>

                                <Select
                                    labelId="collection-cashier-label"
                                    label="Search by Cashier"
                                    value={cashier}
                                    onChange={(event) =>
                                        setCashier(event.target.value)
                                    }
                                >
                                    <MenuItem value="">
                                        All Cashiers
                                    </MenuItem>

                                    {userData.map((user) => (
                                        <MenuItem
                                            key={
                                                user.id ??
                                                user.username
                                            }
                                            value={user.username}
                                        >
                                            {user.username}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* CONSULTANT */}

                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl
                                fullWidth
                                size="small"
                            >
                                <InputLabel id="collection-consultant-label">
                                    Consultant
                                </InputLabel>

                                <Select
                                    labelId="collection-consultant-label"
                                    label="Consultant"
                                    value={consultant}
                                    onChange={(event) =>
                                        setConsultant(event.target.value)
                                    }
                                >
                                    <MenuItem value="all">
                                        All Consultants
                                    </MenuItem>

                                    {consultants.map((name) => (
                                        <MenuItem
                                            key={name}
                                            value={name}
                                        >
                                            {name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* PAYMENT MODE */}

                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl
                                fullWidth
                                size="small"
                            >
                                <InputLabel id="collection-mode-label">
                                    Payment Mode
                                </InputLabel>

                                <Select
                                    labelId="collection-mode-label"
                                    label="Payment Mode"
                                    value={mode}
                                    onChange={(event) =>
                                        setMode(event.target.value)
                                    }
                                >
                                    <MenuItem value="">
                                        All Modes
                                    </MenuItem>

                                    <MenuItem value="cash">
                                        Cash
                                    </MenuItem>

                                    <MenuItem value="card">
                                        Card
                                    </MenuItem>

                                    <MenuItem value="creditpay">
                                        Credit Payment
                                    </MenuItem>

                                    <MenuItem value="UPI">
                                        UPI
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* CLEAR FILTERS */}

                        <Grid item xs={12} sm={6} md={2}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={clearFilters}
                            >
                                Clear Filters
                            </Button>
                        </Grid>

                    </Grid>
                </CardContent>
            </Card>

            {/* COLLECTION REPORT */}

            <Card className="payment-ip-report-card">
                <CardContent>

                    {/* REPORT SUMMARY */}

                    <Box className="payment-ip-summary">
                        <Box>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Collection Report
                            </Typography>

                            <Typography
                                variant="h5"
                                className="payment-ip-grand-total"
                            >
                                ₹{money(categoryTotals.grandTotal)}
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Total Discount: ₹
                                {money(categoryTotals.grandDiscount)}
                            </Typography>
                        </Box>

                        <Chip
                            label={`${filteredData.length} records`}
                            color="primary"
                            variant="outlined"
                        />

                        {consultant !== "all" && (
                            <Chip
                                label={`Consultant: ${consultant}`}
                                color="success"
                                variant="outlined"
                            />
                        )}
                    </Box>

                    {/* CATEGORY CARDS */}

                    <Grid
                        container
                        spacing={1.5}
                        className="payment-ip-category-cards"
                    >
                        {CATEGORIES.map((category) => (
                            <Grid
                                item
                                xs={6}
                                sm={4}
                                md={3}
                                lg={2}
                                key={category}
                            >
                                <Box className="payment-ip-category-card">
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {category}
                                    </Typography>

                                    <Typography variant="h6">
                                        ₹
                                        {money(
                                            categoryTotals[category].paid
                                        )}
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Discount: ₹
                                        {money(
                                            categoryTotals[category]
                                                .discount
                                        )}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                    {/* ERROR */}

                    {error && (
                        <Box className="payment-ip-error">
                            {error}
                        </Box>
                    )}

                    {/* LOADING */}

                    {isLoading ? (
                        <Box className="payment-ip-loading">
                            <CircularProgress size={32} />

                            <Typography variant="body2">
                                Loading collection report...
                            </Typography>
                        </Box>
                    ) : (
                        <>

                            {/* COLLECTION DATAGRID */}

                            <Box className="payment-ip-grid">
                                <DataGrid
                                    rows={filteredData}
                                    columns={columns}
                                    getRowId={(row) => row.id}
                                    autoHeight
                                    slots={{
                                        toolbar: GridToolbar,
                                    }}
                                    pageSizeOptions={[
                                        10,
                                        25,
                                        50,
                                        100,
                                    ]}
                                    initialState={{
                                        pagination: {
                                            paginationModel: {
                                                page: 0,
                                                pageSize: 25,
                                            },
                                        },
                                        columns: {
                                            columnVisibilityModel: {
                                                category_Audiology: false,
                                                category_Dental: false,
                                            },
                                        },
                                    }}
                                    disableRowSelectionOnClick
                                    density="compact"
                                />
                            </Box>

                            {/* CATEGORY TOTALS */}

                            <Box className="payment-ip-summary">
                                {CATEGORIES.map((category) => (
                                    <Typography
                                        variant="body2"
                                        key={category}
                                    >
                                        {category}: ₹
                                        {money(
                                            footerTotals[category]
                                        )}
                                    </Typography>
                                ))}

                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 800 }}
                                >
                                    Total Collection: ₹
                                    {money(categoryTotals.grandTotal)}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 800 }}
                                >
                                    Total Discount: ₹
                                    {money(categoryTotals.grandDiscount)}
                                </Typography>
                            </Box>

                            {/* PAYMENT MODE TOTALS */}

                            <Box
                                className="payment-ip-summary"
                                sx={{
                                    mt: 2,
                                    pt: 2,
                                    borderTop: "1px solid #ddd",
                                    gap: 2,
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 800 }}
                                >
                                    Payment Mode Summary
                                </Typography>

                                <Typography variant="body2">
                                    Cash: ₹
                                    {money(paymentModeTotals.cash)}
                                </Typography>

                                <Typography variant="body2">
                                    Card: ₹
                                    {money(paymentModeTotals.card)}
                                </Typography>

                                <Typography variant="body2">
                                    UPI: ₹
                                    {money(paymentModeTotals.UPI)}
                                </Typography>

                                <Typography variant="body2">
                                    Credit Payment: ₹
                                    {money(
                                        paymentModeTotals.creditpay
                                    )}
                                </Typography>

                                {paymentModeTotals.other > 0 && (
                                    <Typography variant="body2">
                                        Other: ₹
                                        {money(paymentModeTotals.other)}
                                    </Typography>
                                )}
                            </Box>

                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}

export default CollectionAll;
